import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ExternalLink, Eye, Plus } from "lucide-react";
import { api, LANGS } from "@/lib/api";
import { StatusBadge } from "@/components/admin/StatusBadge";

const TYPE_LABELS = {
  page: "Homepage", service: "Services", branch: "Branches",
  project: "Projects", blog: "Blog", brand: "Brands", landing: "Landing Pages",
};
const STATUSES = ["Missing", "Draft", "Review", "Published"];

function LangCell({ status, lang, slug, entityId }) {
  const path = slug ? `/${lang}/${slug}` : `/${lang}`;
  return (
    <div className="flex flex-col items-center gap-1">
      <StatusBadge status={status} />
      {status === "Published" && (
        <a href={path} target="_blank" rel="noopener noreferrer"
          data-testid={`view-${entityId}-${lang}`}
          className="inline-flex items-center gap-0.5 text-[10px] text-emerald-400 hover:underline">
          <ExternalLink size={9} /> View Live
        </a>
      )}
      {(status === "Draft" || status === "Review") && (
        <a href={`${path}?preview=1`} target="_blank" rel="noopener noreferrer"
          data-testid={`preview-${entityId}-${lang}`}
          className="inline-flex items-center gap-0.5 text-[10px] text-amber-400 hover:underline">
          <Eye size={9} /> Preview
        </a>
      )}
      {status === "Missing" && (
        <Link to={`/sbt-panel-7391/entities/${entityId}`}
          data-testid={`create-${entityId}-${lang}`}
          className="inline-flex items-center gap-0.5 text-[10px] text-subtle hover:text-copper hover:underline">
          <Plus size={9} /> Create
        </Link>
      )}
    </div>
  );
}

export default function Entities() {
  const qc = useQueryClient();
  const { data: entities } = useQuery({ queryKey: ["cms-entities"], queryFn: async () => (await api.get("/cms/entities")).data });

  const [langF, setLangF] = useState("all");
  const [statusF, setStatusF] = useState("all");
  const [typeF, setTypeF] = useState("all");
  const [needsReview, setNeedsReview] = useState(false);

  const togglePublish = async (e) => {
    try {
      await api.post(`/cms/entities/${e.entity_id}/${e.published ? "unpublish" : "publish"}`);
      qc.invalidateQueries({ queryKey: ["cms-entities"] });
      toast.success(`${e.entity_id} ${e.published ? "unpublished" : "published"}`);
    } catch (err) { toast.error("Action failed"); }
  };

  const types = useMemo(() => [...new Set((entities || []).map((e) => e.type))], [entities]);

  const filtered = useMemo(() => (entities || []).filter((e) => {
    if (typeF !== "all" && e.type !== typeF) return false;
    if (needsReview && !Object.values(e.status_matrix).includes("Review")) return false;
    const vals = Object.values(e.status_matrix);
    if (langF !== "all" && statusF !== "all") return e.status_matrix[langF] === statusF;
    if (langF === "all" && statusF !== "all") return vals.includes(statusF);
    return true;
  }), [entities, langF, statusF, typeF, needsReview]);

  const grouped = filtered.reduce((acc, e) => { (acc[e.type] ||= []).push(e); return acc; }, {});
  const sel = "bg-obsidian border border-white/15 rounded-lg px-3 py-2 text-sm text-ink focus:border-copper transition-colors";

  return (
    <div>
      <h1 className="text-3xl font-bold uppercase tracking-tight text-ink mb-1">Content</h1>
      <p className="text-subtle text-sm mb-6">Manage entities & per-language translation status</p>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 mb-8" data-testid="entity-filters">
        <div className="flex flex-col gap-1">
          <label className="text-[10px] uppercase text-faint tracking-wide">Language</label>
          <select data-testid="filter-language" value={langF} onChange={(e) => setLangF(e.target.value)} className={sel}>
            <option value="all">All</option>
            {LANGS.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[10px] uppercase text-faint tracking-wide">Status</label>
          <select data-testid="filter-status" value={statusF} onChange={(e) => setStatusF(e.target.value)} className={sel}>
            <option value="all">All</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[10px] uppercase text-faint tracking-wide">Entity</label>
          <select data-testid="filter-type" value={typeF} onChange={(e) => setTypeF(e.target.value)} className={sel}>
            <option value="all">All</option>
            {types.map((t) => <option key={t} value={t}>{TYPE_LABELS[t] || t}</option>)}
          </select>
        </div>
        <button
          data-testid="filter-needs-review"
          onClick={() => setNeedsReview((v) => !v)}
          className={`text-xs px-4 py-2 rounded-lg border transition-colors ${needsReview ? "border-amber-400 text-amber-400 bg-amber-400/10" : "border-white/20 text-subtle hover:text-ink"}`}
        >
          Needs Review
        </button>
        {(langF !== "all" || statusF !== "all" || typeF !== "all" || needsReview) && (
          <button
            data-testid="filter-clear"
            onClick={() => { setLangF("all"); setStatusF("all"); setTypeF("all"); setNeedsReview(false); }}
            className="text-xs px-4 py-2 rounded-lg border border-white/20 text-subtle hover:text-ink"
          >
            Clear
          </button>
        )}
      </div>

      {Object.keys(grouped).length === 0 && (
        <p className="text-subtle text-sm" data-testid="entities-empty">No entities match these filters.</p>
      )}

      {Object.entries(grouped).map(([type, list]) => (
        <div key={type} className="mb-10">
          <p className="eyebrow mb-3">{TYPE_LABELS[type] || type}</p>
          <div className="overflow-x-auto card-surface !hover:translate-y-0">
            <table className="w-full text-sm" data-testid={`entities-table-${type}`}>
              <thead>
                <tr className="text-left text-faint border-b border-white/10">
                  <th className="px-4 py-3 font-medium">Title</th>
                  {LANGS.map((l) => <th key={l} className="px-3 py-3 font-medium uppercase text-center">{l}</th>)}
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((e) => (
                  <tr key={e.entity_id} className="border-b border-white/5 hover:bg-surface" data-testid={`entity-row-${e.entity_id}`}>
                    <td className="px-4 py-3 text-ink align-top">
                      <Link to={`/sbt-panel-7391/entities/${e.entity_id}`} className="hover:text-copper">{e.title}</Link>
                      <div className="text-faint text-[10px] font-mono">{e.entity_id}</div>
                    </td>
                    {LANGS.map((l) => (
                      <td key={l} className="px-3 py-3 text-center align-top">
                        <LangCell status={e.status_matrix[l]} lang={l} slug={e.slugs?.[l] || ""} entityId={e.entity_id} />
                      </td>
                    ))}
                    <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap align-top">
                      <button
                        data-testid={`toggle-publish-${e.entity_id}`}
                        onClick={() => togglePublish(e)}
                        className={`text-xs px-3 py-1.5 rounded-full border ${e.published ? "border-emerald-500/40 text-emerald-400" : "border-white/20 text-subtle"}`}
                      >
                        {e.published ? "Published" : "Unpublished"}
                      </button>
                      <Link to={`/sbt-panel-7391/entities/${e.entity_id}`} data-testid={`edit-${e.entity_id}`} className="text-xs px-3 py-1.5 rounded-full border border-copper/40 text-copper">Edit</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
